import { Link, useParams } from "react-router-dom";
import { useJob, useSharedWorkRecord, useSharedWorkRevisions } from "../hooks/useReads";
import { useT } from "../i18n";
import { savedLaySections, type SavedLayPiece, type SavedLaySection } from "../lib/saved-lay-diagram";
import { usePageHead } from "../shell/Frame";

const pct = (value: number, total: number) => `${Math.max(0, Math.min(100, value / total * 100))}%`;
const mm = (value: number) => `${Number(value.toFixed(1))} mm`;

export function LayDiagram() {
  const { ref } = useParams();
  const { t } = useT();
  const job = useJob(ref);
  const work = useSharedWorkRecord(job.data ? ref : null);
  const revisions = useSharedWorkRevisions(work.data?.id);
  usePageHead(t("lay_title"), ref);

  const back = <Link className="inline-flex min-h-11 items-center text-sm font-semibold text-primary underline" to={`/work-order/${ref}`}>{t("back")}</Link>;
  if (job.isLoading || work.isLoading || revisions.isLoading) return <p className="text-sm text-muted-foreground">{t("loading")}</p>;
  if (!job.data || work.isError || revisions.isError || !work.data) return <div>{back}<p role="alert" className="text-sm text-destructive">{t("site_record_denied_body")}</p></div>;

  // The newest accepted revision is authoritative. An older diagram must not reappear
  // after another device saves a newer version without it.
  const sections = savedLaySections(revisions.data?.[0]?.document);
  return <main className="mx-auto w-full max-w-7xl space-y-5 pb-8">
    {back}
    <div><h1 className="text-2xl font-bold">{t("lay_title")}</h1><p className="text-sm text-muted-foreground">{job.data.address ?? ref}</p></div>
    {!sections.length && <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">{t("lay_empty")}</p>}
    {sections.map(section => <SectionDiagram key={section.id} section={section} />)}
  </main>;
}

function SectionDiagram({ section }: { section: SavedLaySection }) {
  const { t } = useT();
  const lay = section.savedLayDiagram;
  return <section className="rounded-xl border bg-card p-3 sm:p-5" aria-label={section.name}>
    <h2 className="text-xl font-bold">{section.name}</h2>
    <p className="text-sm text-muted-foreground">{t("lay_board", { length: Math.round(section.boardLengthMM), width: Math.round(section.boardWidthMM) })}</p>
    <div className="my-3 flex flex-wrap gap-x-5 gap-y-1 text-xs font-semibold">
      <span className="text-orange-600">■ {t("lay_start_cut")}</span>
      <span className="text-muted-foreground">■ {t("lay_full")}</span>
      <span className="text-teal-600">■ {t("lay_end_cut")}</span>
    </div>
    <p className="mb-2 text-xs font-bold">{t("lay_start_wall")} → {t("lay_end_wall")}</p>
    {lay.cutPairs.length > 0 && <div className="mb-4 space-y-2 rounded-xl border border-orange-300 bg-orange-50 p-3 text-orange-950">
      <h3 className="text-base font-bold">{t("lay_pair_title")}</h3>
      <p className="text-xs">{t("lay_pair_note")}</p>
      {lay.cutPairs.map(pair => <p key={pair.boardNumber} className="font-mono text-sm font-bold">
        {t("lay_pair_board", { board: pair.boardNumber })}: {pair.startSerial} {mm(pair.startLengthMM)} + {mm(pair.kerfMM)} {t("lay_kerf")} + {pair.endSerial} {mm(pair.endLengthMM)}
      </p>)}
    </div>}
    <div className="space-y-3">
      {lay.rows.map(row => <div key={row.rowNumber} className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="w-7 shrink-0 font-mono text-xs font-bold">{row.rowNumber}</span>
          <div className="relative h-8 min-w-0 flex-1 rounded bg-muted/40" aria-label={t("lay_row_length", { row: row.rowNumber, length: Math.round(row.measuredRunMM) })}>
            <div className="absolute inset-y-0 left-0 rounded bg-muted" style={{ width: pct(row.measuredRunMM, lay.longestMeasuredRunMM) }} />
            {row.pieces.map((piece, index) => <Piece key={index} piece={piece} total={lay.longestMeasuredRunMM} />)}
            <div className="absolute -top-0.5 h-9 w-0.5 bg-foreground" style={{ left: pct(row.measuredRunMM, lay.longestMeasuredRunMM) }} />
          </div>
          <span className="w-16 shrink-0 text-right font-mono text-xs">{Math.round(row.measuredRunMM)}</span>
        </div>
        <p className="pl-9 text-xs text-muted-foreground">{t("lay_row_width", { row: row.rowNumber, width: Math.round(row.widthMM) })}</p>
        <div className="flex flex-wrap gap-2 pl-9">
          {row.pieces.filter(p => p.serial).map(p => {
            const pair = lay.cutPairs.find(c => c.startSerial === p.serial || c.endSerial === p.serial);
            return <strong key={p.serial} className={`rounded-lg px-2 py-1 font-mono text-base ${p.kind === "startCut" ? "bg-orange-100 text-orange-800" : "bg-teal-100 text-teal-800"}`}>
              {p.serial} {mm(p.lengthMM)} {pair && <small className="text-xs">{t("lay_pair_board", { board: pair.boardNumber })}</small>}
            </strong>;
          })}
        </div>
        {row.needsOnsiteSetOut && <p className="pl-9 text-xs text-warning">{t("lay_check_on_site", { row: row.rowNumber })}</p>}
      </div>)}
    </div>
    <p className="mt-4 text-xs text-muted-foreground">{t("lay_note", { gap: Math.round(section.wallGapMM) })}</p>
  </section>;
}

function Piece({ piece, total }: { piece: SavedLayPiece; total: number }) {
  const colour = piece.kind === "startCut" ? "bg-orange-500" : piece.kind === "endCut" ? "bg-teal-600" : "bg-slate-400";
  return <div className={`absolute top-0.5 flex h-7 items-center justify-center overflow-hidden rounded-sm border-r border-white/90 text-[10px] font-bold text-white ${colour}`}
    style={{ left: pct(piece.fromStartWallMM, total), width: pct(piece.lengthMM, total) }}
    title={piece.serial ? `${piece.serial} ${mm(piece.lengthMM)}` : mm(piece.lengthMM)}>{piece.serial}</div>;
}
